
import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Fix for Three.js intrinsic elements
const InstancedMesh = 'instancedMesh' as any;
const BoxGeometry = 'boxGeometry' as any;
const MeshBasicMaterial = 'meshBasicMaterial' as any;
const Group = 'group' as any;

// Reduced count for a cleaner, more premium look as requested
const METEOR_COUNT = 45; 
const BASE_COLOR = new THREE.Color('#00f2ff');
const GLOW_INTENSITY = 2.0; // High intensity for bloom interaction

interface MeteorData {
  pos: THREE.Vector3;
  speed: number;
  length: number;
  width: number;
}

const Starfield: React.FC = () => {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  
  // Initialize meteor data with very slow speeds and varied dimensions
  const meteors = useMemo(() => {
    const data: MeteorData[] = [];
    for (let i = 0; i < METEOR_COUNT; i++) {
      data.push({
        pos: new THREE.Vector3(
          (Math.random() - 0.5) * 50,
          (Math.random() - 0.5) * 45,
          (Math.random() - 0.5) * 15
        ),
        // Significantly slower falling speed for a "slowly" effect
        speed: 0.01 + Math.random() * 0.025,
        length: 2.0 + Math.random() * 5.0, 
        width: 0.015 + Math.random() * 0.02,
      });
    }
    return data;
  }, []);

  const tempObject = new THREE.Object3D();
  const tempColor = new THREE.Color();

  useFrame((state) => {
    if (!meshRef.current) return;

    meteors.forEach((meteor, i) => {
      // Slow vertical fall
      meteor.pos.y -= meteor.speed;
      
      // Reset if it goes too far down
      if (meteor.pos.y < -25) {
        meteor.pos.y = 25;
        meteor.pos.x = (Math.random() - 0.5) * 50;
      }

      // Calculate Fade Factor based on Y position (Fade in at top, fade out at bottom)
      // Range: 25 to -25. Alpha is 0 at boundaries, 1 at center.
      const boundary = 25;
      const margin = 12; // Distance over which it fades
      const distFromEdge = boundary - Math.abs(meteor.pos.y);
      const alpha = THREE.MathUtils.clamp(distFromEdge / margin, 0, 1);

      // Apply transformation
      tempObject.position.copy(meteor.pos);
      tempObject.scale.set(meteor.width, meteor.length, meteor.width);
      tempObject.updateMatrix();
      meshRef.current!.setMatrixAt(i, tempObject.matrix);

      // Apply color fading via intensity
      // Darker colors don't trigger the bloom effect, effectively fading them out
      tempColor.copy(BASE_COLOR).multiplyScalar(alpha * GLOW_INTENSITY);
      meshRef.current!.setColorAt(i, tempColor);
    });

    meshRef.current.instanceMatrix.needsUpdate = true;
    // Essential: Notify Three.js that the color buffer needs updating
    if (meshRef.current.instanceColor) {
      meshRef.current.instanceColor.needsUpdate = true;
    }
  });

  return (
    <Group 
      rotation={[0, 0, Math.PI / 3.5]} // Elegant diagonal angle
      position={[0, 0, -8]} // Pushed back further for scale
    >
      <InstancedMesh ref={meshRef} args={[null, null, METEOR_COUNT]}>
        <BoxGeometry args={[1, 1, 1]} />
        <MeshBasicMaterial transparent={true} toneMapped={false} />
      </InstancedMesh>
    </Group>
  );
};

export default Starfield;
